import type { ProgressEvent } from '@shefira/shared';

import { element, kitChip, kitHeading, kitIcon, type KitLine, kitLines } from './kit.js';
import { formatCount } from './profile-format.js';
import { uiText } from './text.js';

/**
 * The pieces of the profile window, built afresh at every render: the XP
 * bar, the three stats, the career lines and the hunting record. Pure DOM,
 * nothing decided here (the numbers arrive computed).
 */

export type ProfileLine = KitLine;

export function hint(text: string): HTMLElement {
  return element('p', 'login__hint profile__small', text);
}

export function xpBar(progress: ProgressEvent | undefined): HTMLElement {
  const bar = element('div', 'profile__xp');
  const fill = element('span', 'profile__xp-fill');
  const label = element('span', 'profile__xp-label');
  if (progress === undefined) {
    label.textContent = '…';
  } else {
    const span = progress.nextLevelXp - progress.levelStartXp;
    const into = progress.xp - progress.levelStartXp;
    // The level cap has no next level: a full bar rather than a division by zero.
    const share = span > 0 ? Math.min(1, Math.max(0, into / span)) : 1;
    fill.style.width = `${String(Math.round(share * 100))}%`;
    label.textContent =
      span > 0 ? `${formatCount(into)} / ${formatCount(span)} XP` : uiText('Niveau maximum');
  }
  bar.append(fill, label);
  return bar;
}

/** The three stats, large, each in its colour (`tone` picks it in the CSS). */
export function statCards(
  stats: readonly (ProfileLine & { tone: 'health' | 'dps' | 'defence' })[],
): HTMLElement {
  const row = element('dl', 'profile__stats');
  for (const stat of stats) {
    const card = element('div', `profile__stat profile__stat--${stat.tone}`);
    const text = element('div', 'profile__stat-text');
    text.append(element('dd', 'profile__stat-value', stat.value), element('dt', '', stat.label));
    card.append(kitIcon(stat.icon), text);
    row.append(card);
  }
  return row;
}

/** Small counters beside each other: gold, portals. */
export function chips(lines: readonly ProfileLine[]): HTMLElement {
  const row = element('div', 'profile__chips');
  row.append(...lines.map(kitChip));
  return row;
}

export function lines(title: string, entries: readonly ProfileLine[]): HTMLElement {
  const section = element('section', 'profile__section');
  section.append(kitHeading(title), kitLines(entries));
  return section;
}

/** The prey most hunted, ranked, each with a bar against the first. */
export function huntingRecord(
  prey: readonly { name: string; count: number }[] | undefined,
): HTMLElement {
  const section = element('section', 'profile__section');
  section.append(kitHeading('Tableau de chasse'));
  if (prey === undefined || prey.length === 0) {
    section.append(hint(prey === undefined ? '…' : 'Aucun ennemi vaincu pour l’instant.'));
    return section;
  }
  const top = prey[0]?.count ?? 1;
  const list = element('ol', 'profile__prey');
  prey.forEach(({ name, count }, index) => {
    const row = element('li', 'profile__prey-row');
    const rank = element('span', `profile__rank profile__rank--${String(index + 1)}`);
    rank.textContent = String(index + 1);
    const body = element('div', 'profile__prey-body');
    const topLine = element('div', 'profile__prey-top');
    topLine.append(
      element('span', 'profile__prey-name', name),
      element('span', 'profile__prey-count', formatCount(count)),
    );
    const bar = element('span', 'profile__prey-bar');
    const fill = element('span', '');
    fill.style.width = `${String(Math.max(4, Math.round((count / top) * 100)))}%`;
    bar.append(fill);
    body.append(topLine, bar);
    row.append(rank, body);
    list.append(row);
  });
  section.append(list);
  return section;
}
